import { useEffect, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { Pagination, Form, Row, Col, Card, Table, Accordion } from "react-bootstrap";
import Paginator from "paginator";
import { useTranslation } from 'react-i18next';
import { PaginatorObject, TableContainerProps } from "src/types";
import { TableOptions, useBlockLayout, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import TableSkeleton from "src/components/TableSkeleton/TableSkeleton";
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import LeadFilters from "./LeadFilters";
import useTableStore from "src/store/TableStore";
import { LeadSearchParams } from "src/api";

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label';

const isFromInteractiveElement = (e: ReactMouseEvent) =>
  (e.target as Element).closest(INTERACTIVE_SELECTOR) !== null;

const isNewTabClick = (e: ReactMouseEvent) => e.ctrlKey || e.metaKey;


const LeadsTable = ({
  loadFn,
  totalCount,
  columns,
  data,
  hiddenColumns = ['id'],
  error,
  initPageSize = 50,
  useFixedColumn,
  loading,
  noBorder,
  className = '',
  dblClickCallback,
  dbClickType = 'double',
  filter,
  onResetFilter,
  activeFiltersCount,
  rowHref,
  ...props
}: TableContainerProps & {
  loadFn: (filter: Partial<LeadSearchParams>) => void;
  totalCount: number;
  useFixedColumn?: boolean;
  initPageSize?: number;
  noBorder?: boolean;
  className?: string;
  dbClickType?: string;
  dblClickCallback?: (row?: any) => void;
  filter?: Partial<LeadSearchParams>;
  onResetFilter?: () => void;
  activeFiltersCount?: number
  rowHref?: (row: any) => string;
}) => {
  const [pgnt, setPgnt] = useState<PaginatorObject>();
  const { t } = useTranslation();

  const initialFilter = Object.assign({
    FirstName: '',
    LastName: '',
    Email: '',
    Phone: '',
    Codice_Fiscale__c: '',
    Partita_Iva__c: '',
    Company: '',
    CreatedDate: { from: '', to: '' },
    City: '',
    LeadSource: '',
    Marca__c: '',
    Modello__c: '',
    salesUser: "",
    Filiale_Dealer_Titolare__c: '',
  }, filter);

  const sort = filter?.orderBy
    ? [{ id: filter?.orderBy, desc: filter?.ascending === false }]
    : undefined;

  const tableOptions: TableOptions<Object> = {
    columns,
    data,
    autoResetPage: false,
    autoResetGlobalFilter: false,
    manualSortBy: true,
    initialState: {
      pageSize: filter?.limit || initPageSize,
      hiddenColumns,
      ...(sort ? { sortBy: sort } : {})
    },
    ...props,
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    setPageSize,
    toggleAllRowsSelected,
    setSortBy,
  } = useTable(
    tableOptions,
    useSortBy,
    useFixedColumn ? useBlockLayout : () => { },
    usePagination,
    useRowSelect,
  );

  const { setClearSelectedRowsCallback } = useTableStore();

  useEffect(() => {
    setClearSelectedRowsCallback(toggleAllRowsSelected)
  }, [setClearSelectedRowsCallback, toggleAllRowsSelected])

  const gotoPage = (val: number, size = initPageSize) => {
    const pg = new Paginator(size, 1);
    let newPgt: PaginatorObject = pg.build(totalCount, val)

    loadFn({ skip: newPgt.first_result, limit: size });
    setPgnt(newPgt)
  }

  const movePage = (next: boolean) => {
    if (!pgnt) return;

    const pg = new Paginator(state.pageSize, 1);

    let newPgt: PaginatorObject
    let nextPage = next ? pgnt.next_page : pgnt.previous_page

    newPgt = pg.build(totalCount, nextPage)
    loadFn({ skip: newPgt.first_result, limit: state.pageSize });

    setPgnt(newPgt)
  }

  useEffect(() => {
    const sortField = state?.sortBy?.[0]?.id;
    const sortDirection = state?.sortBy?.[0]?.desc;

    const filterSortField = filter?.orderBy;
    const filterSortDirection = filter?.ascending === true ? false : true;

    if (!sortField) return;

    if (sortField === filterSortField && sortDirection === filterSortDirection) {
      return;
    }

    loadFn({
      skip: 0,
      orderBy: sortField,
      ascending: sortDirection ? false : true
    })

    const pg = new Paginator(state.pageSize, 1);
    const res = pg.build(totalCount, 1);
    setPgnt(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.sortBy])

  useEffect(() => {
    const pg = new Paginator(state.pageSize, 1);
    const res = pg.build(totalCount, 1);
    setPgnt(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount, state.pageSize])

  return (
    <div>
      {error
        ? (
          <Row className="mt-4">
            <Col>
              <div className="table-error-message">{error?.message}</div>
            </Col>
          </Row>
        )
        : null
      }
      <Accordion className="mt-4" flush>
        <LeadFilters
          initialFilter={initialFilter}
          activeFiltersCount={activeFiltersCount}
          onResetFilter={() => {
            setPageSize(50);
            setSortBy([]);
            onResetFilter?.();
          }}
          isLoading={loading}
          headerGroups={headerGroups}
          onFiltersChange={(filters) => {
            toggleAllRowsSelected(false)
            // @ts-ignore
            loadFn(filters)
            console.debug('leads.filters.change', filters)
          }}
        />
      </Accordion>

      {(!loading && !error) ? (
        <div className="table-results-container mt-2">
          {t('result_count')?.toLocaleUpperCase()}:{' '}
          <span className="table-result-count">{totalCount}</span>
        </div>
      ) : null}

      {loading ? <TableSkeleton rows={initPageSize} /> : null}

      {!loading && !error && (
        <>
          <Row>
            <Col>
              <Card className={'mt-2' + (noBorder ? ' table-no-border' : '')}>
                <div className="flipped-container flipped">
                  <Table
                    striped
                    borderless
                    hover
                    responsive
                    {...getTableProps()}
                    className={className + ' m-0'}
                  >
                    <thead className="border-bottom">
                      {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column: any) => (
                            <th
                              className="text-nowrap"
                              {...column.getHeaderProps({
                                ...column.getSortByToggleProps({ title: undefined }),
                                className: column.className,
                              })}
                            >
                              {column.render('Header')}
                              {column.canSort ? (
                                <SortingControls column={column} />
                              ) : null}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row, i) => {
                        prepareRow(row);
                        return (
                          <tr
                            className="role-button"
                            onClick={(e) => {
                              if (rowHref && isNewTabClick(e)) {
                                if (!isFromInteractiveElement(e)) {
                                  window.open(rowHref(row.original), '_blank', 'noopener');
                                }
                                return;
                              }
                              dbClickType === 'single' && dblClickCallback && dblClickCallback(row.original)
                            }}
                            onDoubleClick={(e) => {
                              if (rowHref && isNewTabClick(e)) return;
                              dbClickType === 'double' && dblClickCallback && dblClickCallback(row.original)
                            }}
                            onMouseDown={(e) => {
                              if (rowHref && e.button === 1 && !isFromInteractiveElement(e)) e.preventDefault();
                            }}
                            onAuxClick={(e) => {
                              if (rowHref && e.button === 1 && !isFromInteractiveElement(e)) {
                                window.open(rowHref(row.original), '_blank', 'noopener');
                              }
                            }}
                            {...row.getRowProps()}
                          >
                            {row.cells.map((cell: any) => {
                              return (
                                <td
                                  className="text-nowrap"
                                  {...cell.getCellProps({
                                    className: cell.column.className,
                                  })}
                                >
                                  {cell.render('Cell')}
                                </td>
                              )
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
          <div className="mt-2 float-end" style={{ pointerEvents: loading ? 'none' : 'all' }}>
            <Pagination>
              <Form.Select
                className="w-auto page-select"
                value={state.pageSize}
                onChange={(e) => {
                  const newLimit = Number(e.target.value)
                  setPageSize(newLimit)
                  loadFn({ skip: 0, limit: newLimit });
                }}
              >
                {[10, 20, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {t('show')} {pageSize}
                  </option>
                ))}
              </Form.Select>
              <Form.Label className="text-nowrap mx-3 mt-2">
                {t('page')}
                <strong className="ms-1">
                  {pgnt?.current_page} {t('of')} {pgnt?.total_pages}
                </strong>
              </Form.Label>

              <Pagination.First
                onClick={() => gotoPage(1)}
                disabled={!pgnt?.has_previous_page}
              />
              <Pagination.Prev
                onClick={() => movePage(false)}
                disabled={!pgnt?.has_previous_page}
              />
              <Pagination.Next
                onClick={() => movePage(true)}
                disabled={!pgnt?.has_next_page}
              />
              <Pagination.Last
                onClick={() => gotoPage(pgnt?.total_pages!)}
                disabled={!pgnt?.has_next_page}
              />
            </Pagination>
          </div>
        </>
      )}
    </div>
  )
}

const SortingControls = ({ column }) => {
  return (
    <span className="position-relative">
      {column.isSorted ? (
        column.isSortedDesc ? (
          <>
            <FaSortUp className="mt-1 opacity-25 position-absolute" />
            <FaSortDown className="mt-1 position-absolute" />
          </>
        ) : (
          <>
            <FaSortUp className="mt-1 position-absolute" />
            <FaSortDown className="mt-1 opacity-25 position-absolute" />
          </>
        )
      ) : (
        <>
          <FaSortUp className="mt-1 opacity-25 position-absolute" />
          <FaSortDown className="mt-1 opacity-25 position-absolute" />
        </>
      )}
    </span>
  );
};


export default LeadsTable;
